/*
  ESTA FOLHA É CÓPIA DO NOTEBOOK.

  Origem: ../adsa-financas/src/shared/types.ts

  **Por que copiar em vez de reescrever:** este arquivo já estava duplicado à mão neste
  repositório, e duplicação à mão de cargo, permissão, data e dinheiro é defeito que aparece como
  "o pastor não entra" ou "o número do painel não bate com o do programa". Fase 3 do espelho,
  pedida pelo Cristiano em 12/10/2026: compartilhar só o que o painel já copiava à mão, **sem
  reorganizar o notebook**.

  **MEXEU LÁ, MEXA AQUI.** E há prova disso em `testes/copia-do-notebook.test.ts`: ela lê os dois
  arquivos e fica vermelha quando o que está abaixo da marca deixa de ser igual ao original.

  **O `@ts-nocheck` saiu em 13/10/2026, e isso é notícia boa.** Este repositório liga quatro
  travas de compilador (`noUncheckedIndexedAccess`, `exactOptionalPropertyTypes`,
  `isolatedModules`, `verbatimModuleSyntax`), e até a entrega 2.11 o notebook não as ligava: a
  cópia exata dava 19 erros só por causa delas, e consertá-los aqui quebraria a exatidão, que é a
  coisa toda que esta cópia existe para ter. Então o arquivo entrava com `@ts-nocheck`, e um erro
  de tipo dentro dele não era visto aqui.

  A sessão do notebook ligou as mesmas quatro travas lá, e agora a cópia exata compila inteira,
  com tudo ligado. **Se um dia ela voltar a não compilar, não ponha `@ts-nocheck` de volta sem
  pensar**: o que isso quer dizer é que os dois lados se separaram de novo, e o conserto é lá.

  Nada daqui para baixo se edita à mão: copie o arquivo do notebook inteiro por cima e rode a
  prova.
*/
/* ==== FIM DO CABECALHO DA COPIA: daqui para baixo e copia exata do notebook ==== */
export type FormaEntrada = 'CARTAO' | 'PIX' | 'TRANSF' | 'ESPECIE' | 'MOEDA'

/**
 * Como o dízimo ou voto entrou.
 *
 * PIX e transferência viraram uma coisa só: os dois caem no banco no mesmo dia e no
 * mesmo extrato. A separação que importa na conferência é **banco × espécie**, e essa
 * continua. (A folha de ofertas é outra história e mantém PIX separado — lá não existe
 * transferência.)
 */
export type FormaDizimo = 'CARTAO' | 'PIX_TRANSF' | 'ESPECIE'

export const FORMAS_DIZIMO: { chave: FormaDizimo; titulo: string; porExtenso: string }[] = [
  { chave: 'CARTAO', titulo: 'CARTÃO', porExtenso: 'Cartão' },
  { chave: 'PIX_TRANSF', titulo: 'PIX/TRANSF.', porExtenso: 'PIX / Transferência' },
  { chave: 'ESPECIE', titulo: 'ESPEC.', porExtenso: 'Espécie' }
]

/** Como a saída foi paga. Mesma lógica dos dízimos: PIX e transferência saem do banco. */
export type FormaSaida = 'ESPECIE' | 'PIX_TRANSF' | 'CARTAO'

export const FORMAS_SAIDA: { chave: FormaSaida; porExtenso: string }[] = [
  { chave: 'ESPECIE', porExtenso: 'Espécie' },
  { chave: 'PIX_TRANSF', porExtenso: 'PIX / Transferência' },
  { chave: 'CARTAO', porExtenso: 'Cartão' }
]

/**
 * De onde saiu o dinheiro de uma saída — os três "bolsos" (Etapa 12.2).
 *
 * É diferente de QUEM ARCA com a despesa: a sede paga a luz de Palmeiras pela conta do
 * Campo, e a despesa é de Palmeiras. Quem arca é o `congregacao_id` da saída.
 *
 *   CAIXA        o dinheiro em espécie da congregação — é o único que sai do saldo dela
 *   CARTAO       o cartão recarregável da congregação, abastecido pela conta do Campo
 *   CONTA_CAMPO  a conta bancária do Campo — só quem trabalha em todo o Campo lança daqui
 */
export type Bolso = 'CAIXA' | 'CARTAO' | 'CONTA_CAMPO'

export const BOLSOS: { chave: Bolso; porExtenso: string; curto: string }[] = [
  { chave: 'CAIXA', porExtenso: 'Caixa da congregação (dinheiro)', curto: 'caixa' },
  { chave: 'CARTAO', porExtenso: 'Cartão recarregável da congregação', curto: 'cartão' },
  { chave: 'CONTA_CAMPO', porExtenso: 'Conta do Campo', curto: 'conta do Campo' }
]

/**
 * A forma de pagamento que acompanha cada bolso. A tela não pergunta mais "como foi
 * pago": o bolso já diz — caixa é espécie, cartão é cartão, e da conta do Campo sai por
 * PIX ou transferência. A coluna continua no banco para os lançamentos antigos.
 */
export function formaDoBolso(bolso: Bolso): FormaSaida {
  return bolso === 'CAIXA' ? 'ESPECIE' : bolso === 'CARTAO' ? 'CARTAO' : 'PIX_TRANSF'
}

/** Como a despesa geral do Campo aparece, no lugar do nome de uma congregação. */
export const CUSTO_GERAL_DO_CAMPO = 'Campo (despesa geral)'

/**
 * Um departamento que recebe oferta com destino: CIBOC, SEMADSA (Missões), JUADSA, GEADSA.
 * Nome oficial e apelido separados; onde couber, os dois — "SEMADSA (Missões)".
 */
export function nomeDoDepartamento(d: { nomeOficial: string; apelido: string | null }): string {
  return d.apelido ? `${d.nomeOficial} (${d.apelido})` : d.nomeOficial
}

export type TipoDizimo = 'DIZIMO' | 'VOTO'
/**
 * A situação de uma saída. `APROVADA_AUTORIDADE` nasceu na parte 2.5: quem lança tendo a permissão
 * de aprovar no Campo inteiro sem limite já lança aprovado. Não é a autonomia, que é um valor em
 * reais que a pessoa gasta sozinha; é o cargo. Escrever "aprovada na autonomia" numa saída de
 * R$ 5.000,00 de quem tem R$ 200,00 de autonomia seria o sistema contando uma coisa que não houve.
 */
export type StatusSaida = 'PENDENTE' | 'APROVADA_AUTONOMIA' | 'APROVADA_AUTORIDADE' | 'APROVADA_PRE_AUTORIZACAO' | 'APROVADA_MANUAL' | 'REJEITADA' | 'ESTORNADA'
export type StatusRepasse = 'PENDENTE' | 'APROVADO' | 'PAGO' | 'ESTORNADO'
export type StatusFechamento = 'ABERTO' | 'FECHADO' | 'REABERTO'
/**
 * Como cada congregação participa do repasse. Na prática nem toda congregação repassa
 * percentual: umas pagam valor fixo, outras não repassam nada, e há as que RECEBEM ajuda
 * da sede. Os quatro casos são regra registrada, com vigência e justificativa — nenhum
 * deles é "esquecimento".
 */
export type TipoRegraRepasse = 'PERCENTUAL' | 'VALOR_FIXO' | 'ISENTA' | 'AJUDA'

export const NOMES_TIPO_REGRA: Record<TipoRegraRepasse, string> = {
  PERCENTUAL: 'Percentual sobre o que entrou',
  VALOR_FIXO: 'Valor fixo por mês',
  ISENTA: 'Não repassa (isenta)',
  AJUDA: 'Recebe ajuda da sede'
}

/**
 * A frase do percentual fora da faixa (0.3.17). Antes chegava à tela o texto do zod, em inglês
 * e em pontos-base: "Too big: expected number to be <=10000".
 */
export const PERCENTUAL_FORA_DA_FAIXA = 'O percentual vai de 0 a 100. Escreva só o número, como 15 ou 12,5.'

/** Para onde vai o repasse calculado por uma regra. */
export type DestinoRegra = 'SEDE' | 'MATRIZ'

/**
 * A Igreja Matriz é o nível acima do Campo e é destino EXTERNO: o sistema não cuida das
 * finanças dela, só registra e comprova o envio.
 */
export const NOME_MATRIZ = 'Igreja Matriz'

/** As três direções que um repasse pode ter. */
export type DirecaoRepasse =
  | 'CONGREGACAO_PARA_SEDE'
  | 'SEDE_PARA_CONGREGACAO'
  | 'SEDE_PARA_MATRIZ'
  /** Recarga do cartão de uma congregação, com dinheiro da conta do Campo (Etapa 12.2). */
  | 'CAMPO_PARA_CARTAO'
  | 'NENHUMA'

/**
 * O resumo da folha de repasses separado pela situação (0.3.17). Até a 0.3.16 ele somava
 * aguardando, aprovado e pago num número só, e o repasse ainda não aprovado já aparecia
 * como "recebido".
 */
export interface RepassePorSituacao {
  aguardando: Centavos
  aprovado: Centavos
  /** O que foi pago de verdade: o valor pago, quando é diferente do calculado. */
  pago: Centavos
}

export const NOMES_DIRECAO: Record<DirecaoRepasse, string> = {
  CONGREGACAO_PARA_SEDE: 'Congregação envia para a sede',
  SEDE_PARA_CONGREGACAO: 'Sede envia para a congregação',
  SEDE_PARA_MATRIZ: 'Sede envia para a Igreja Matriz',
  CAMPO_PARA_CARTAO: 'Recarga do cartão da congregação',
  NENHUMA: 'Sem repasse'
}

/** Valores monetários são guardados em CENTAVOS (inteiro) para evitar erro de ponto flutuante. */
export type Centavos = number

export interface LinhaOferta {
  cultoId: string
  data: string // YYYY-MM-DD
  departamentoOuPeriodo: string
  diaSemana: string
  cartao: Centavos
  pix: Centavos
  especie: Centavos
  moeda: Centavos
  total: Centavos
}

export interface LinhaDizimo {
  id: string
  data: string
  membroId: string
  nome: string
  tipo: TipoDizimo
  forma: FormaDizimo
  valor: Centavos
}

/**
 * O total de uma linha da folha de ofertas. Fica aqui, e não em cada lado, para a conta
 * da tela e a conta que o banco grava serem literalmente a mesma linha de código.
 */
export function totalDaOferta(l: Pick<LinhaOferta, 'cartao' | 'pix' | 'especie' | 'moeda'>): Centavos {
  return l.cartao + l.pix + l.especie + l.moeda
}

export interface TotaisOfertas {
  cartao: Centavos
  pix: Centavos
  especie: Centavos
  moeda: Centavos
  total: Centavos
}

/** Os totais do rodapé da folha, coluna por coluna. */
export function totaisDasOfertas(linhas: Pick<LinhaOferta, 'cartao' | 'pix' | 'especie' | 'moeda'>[]): TotaisOfertas {
  const t: TotaisOfertas = { cartao: 0, pix: 0, especie: 0, moeda: 0, total: 0 }
  for (const l of linhas) {
    t.cartao += l.cartao
    t.pix += l.pix
    t.especie += l.especie
    t.moeda += l.moeda
    t.total += totalDaOferta(l)
  }
  return t
}

export const DIAS_SEMANA = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'] as const

export function diaSemana(dataISO: string): string {
  const [y = NaN, m = NaN, d = NaN] = dataISO.split('-').map(Number)
  return DIAS_SEMANA[new Date(y, m - 1, d).getDay()] ?? ''
}

export function formatarReais(centavos: Centavos): string {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function parseReais(texto: string): Centavos {
  const limpo = texto.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.')
  const n = Number(limpo)
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100)
}

/** Como cada situação de repasse aparece escrita no papel e na tela. */
export const NOMES_STATUS_REPASSE: Record<StatusRepasse, string> = {
  PENDENTE: 'aguardando aprovação',
  APROVADO: 'aprovado, ainda não pago',
  PAGO: 'pago',
  ESTORNADO: 'estornado'
}

/**
 * Uma linha de repasse como ela vai IMPRESSA no relatório de movimentação.
 *
 * É uma cópia congelada na hora da assinatura, não um espelho do razão: um repasse pago
 * em outubro não pode fazer o relatório de agosto dizer "pago" quando ele for reimpresso.
 * Por isso o papel diz, com todas as letras, "situação na data da assinatura".
 */
export interface LinhaDeRepasseNoPapel {
  direcao: DirecaoRepasse
  direcaoPorExtenso: string
  origemNome: string
  destinoNome: string
  valor: Centavos
  base: Centavos
  situacao: string
  avulso: boolean
  motivo: string | null
  departamentoDestino: string | null
  /** A regra que gerou o valor, em uma linha: "20% sobre dízimos e votos". */
  regra: string
}

/** O cartão recarregável no fechamento: o que entrou, o que saiu, e o que sobrou nele. */
export interface CartaoNoFechamento {
  descricao: string
  saldoAnterior: Centavos
  recargas: Centavos
  gastos: Centavos
  saldoFinal: Centavos
}

/** O detalhe congelado junto com a fotografia do fechamento. */
export interface DetalheDoFechamento {
  /** As saídas do CAIXA por categoria — as que diminuem o saldo da congregação. */
  saidasPorCategoria: { categoria: string; valor: Centavos }[]
  repasses: LinhaDeRepasseNoPapel[]
  /*
   * Os três blocos da Etapa 12.2. Opcionais porque os fechamentos assinados antes dela
   * não os têm — e o papel de uma versão antiga não pode inventar o que não foi guardado.
   */
  /** Oferta de culto marcado com destino: sai do livro da congregação e vai ao departamento. */
  ofertasComDestino?: { departamento: string; valor: Centavos; departamentoId?: string }[]
  /** Nulo quando a congregação não tem cartão nem movimento de cartão no mês. */
  cartao?: CartaoNoFechamento | null
  /** Pagas pela conta do Campo em nome desta congregação: custo dela, fora do caixa dela. */
  pagasPeloCampo?: { categoria: string; valor: Centavos }[]
}

// ------------------------------------------------- Etapa 12.3 — fechamento do Campo

/** De onde uma linha do Fluxo do Caixa tira o valor sozinha. Ver `linhas_do_fluxo`. */
export type FonteDaLinhaDoFluxo = 'CATEGORIAS' | 'DEPARTAMENTO' | 'MATRIZ' | 'RECARGAS' | 'DIGITADA' | 'NAO_IDENTIFICADA'

/** Uma coluna de departamento do Movimento do Caixa. */
export interface ColunaDeDepartamento {
  id: string
  /** O título da coluna: "CIBOC", "MISSÕES". */
  titulo: string
  /** A linha de cima: "Oferta 1º Domingo". */
  rotulo: string | null
  conta: string | null
  nomeOficial: string
}

/** Uma congregação no Movimento do Caixa, tirada do fechamento assinado dela. */
export interface LinhaDoMovimentoDoCaixa {
  congregacaoId: string
  nome: string
  /** Assinou o mês? Quem não fechou aparece zerado, com a marcação "(não fechou)" também no papel (0.3.17). */
  fechou: boolean
  /**
   * Não fechou, mas o Campo travou os lançamentos do mês (Etapa 12.3a): entra com os números
   * de agora, que não mudam mais.
   */
  travada: boolean
  versao: number | null
  /** Na ordem das colunas. */
  porDepartamento: Centavos[]
  /** Tudo o que entrou: ofertas (com as de destino), dízimos e votos. */
  entrada: Centavos
  /** Só o caixa. O cartão aparece no Fluxo, pelas recargas. */
  saida: Centavos
  saldo: Centavos
}

export interface TotalDoMovimentoDoCaixa {
  porDepartamento: Centavos[]
  entrada: Centavos
  saida: Centavos
  saldo: Centavos
}

export type TipoDaLinhaDoFluxo = 'SALDO_ANTERIOR' | 'SALDO_DO_MES' | 'LINHA'

/** Uma linha do Fluxo do Caixa: o extrato do mês da conta do Campo. */
export interface LinhaDoFluxoDoCaixa {
  /** Identifica a linha entre a tela, a fotografia e a comparação depois da assinatura. */
  chave: string
  tipo: TipoDaLinhaDoFluxo
  /** A linha memorizada; nula na linha que o sistema criou para uma categoria sem linha. */
  linhaId: string | null
  descricao: string
  /** AAAA-MM-DD; nula = "-" no papel. Padrão: dia 1 no saldo anterior, último dia nas linhas com valor. */
  data: string | null
  /** Quem fecha corrigiu a data (Etapa 12.4c). Ausente nas fotografias antigas. */
  dataDigitada?: boolean
  entrada: Centavos
  saida: Centavos
  /** Saldo corrente depois desta linha. */
  saldo: Centavos
  fonte: FonteDaLinhaDoFluxo | null
  /** O que o sistema calcula; nulo quando a linha não tem de onde tirar o valor. */
  automatico: { entrada: Centavos; saida: Centavos } | null
  /** Alguém digitou o valor por cima do automático. */
  digitado: boolean
  /** De onde veio o valor automático, em uma linha: "2 saídas pela conta do Campo". */
  origem: string | null
  /** As categorias de saída que somaram nesta linha no mês. */
  categorias: { normalizada: string; nome: string; valor: Centavos }[]
  /** O sistema acrescentou a linha porque havia dinheiro do mês sem linha. */
  nova: boolean
  /** Só nas linhas de envio de oferta: a coluna de onde vem o valor (fórmula do Excel). */
  departamentoId: string | null
}

/** Os textos do alto e das assinaturas do fechamento do Campo. */
export interface CabecalhoDoCampo {
  /** As três linhas de cima: "IGREJA EVANGÉLICA ASSEMBLEIA DE DEUS"… */
  linhas: string[]
  /** "Capão Redondo" — vai em "Campo: Capão Redondo". */
  campo: string
  /** O nome que vai na assinatura do Pastor Presidente do Campo. */
  pastor: string
  /** Os títulos das duas folhas: "Movimento do Caixa", "Fluxo do Caixa". */
  tituloMovimento: string
  tituloFluxo: string
  /** A linha embaixo do nome do pastor: "Pastor Presidente do Campo". */
  cargoDoPastor: string
  /** A segunda assinatura: "Tesouraria Geral". */
  segundaAssinatura: string
  /** As duas primeiras linhas do Fluxo. */
  linhaSaldoAnterior: string
  linhaSaldoDoMes: string
}

/** O fechamento do Campo como foi assinado. */
export interface DetalheDoFechamentoDoCampo {
  cabecalho: CabecalhoDoCampo
  colunas: ColunaDeDepartamento[]
  movimento: LinhaDoMovimentoDoCaixa[]
  totalDoMovimento: TotalDoMovimentoDoCaixa
  fluxo: LinhaDoFluxoDoCaixa[]
  saldoAnterior: Centavos
  totalEntradas: Centavos
  totalSaidas: Centavos
  saldoFinal: Centavos
}
